import { Select, SelectFooterAction, SelectItem } from "@prismicio/editor-ui"
import { useMemo, useState } from "react"

import type { Locale } from "../../../app/services/locales"
import { AddCustomLocaleDialog } from "./AddCustomLocaleDialog"
import { Flag } from "./Flag"

export interface SelectedLocale {
  id: string
  customName: string | null
}

interface LocaleSelectionProps {
  locale: SelectedLocale | undefined
  isEnabled?: boolean
  onLocaleChange: (selectedLocale: SelectedLocale) => void
  locales: Locale[]
  addCustomLocaleEnabled?: boolean
}

export function LocaleSelection(props: LocaleSelectionProps) {
  const {
    locale,
    isEnabled = true,
    onLocaleChange,
    locales,
    addCustomLocaleEnabled = false,
  } = props

  const selectOptions = useMemo(() => {
    if (!locale?.customName) return locales
    const customLocale = { id: locale.id, label: locale.customName }
    return [...locales, customLocale]
  }, [locale, locales])

  const [isCustomLocaleFormOpen, setIsCustomLocaleFormOpen] = useState(false)

  return (
    <>
      <Select
        color="grey"
        size="large"
        value={locale?.id ?? ""}
        disabled={!isEnabled}
        renderStartIcon={locale ? () => <Flag locale={locale.id} /> : undefined}
        onValueChange={(id) => onLocaleChange({ id, customName: null })}
        placeholder="Select a new locale"
        sideOffset={8}
        side="bottom"
        constrainContentWidth
        flexContent
      >
        <LocaleOptions locales={selectOptions} />

        {addCustomLocaleEnabled && (
          <SelectFooterAction
            iconName="add"
            label="Add custom locale"
            onClick={() => setIsCustomLocaleFormOpen(true)}
          />
        )}
      </Select>

      {addCustomLocaleEnabled && (
        <AddCustomLocaleDialog
          open={isCustomLocaleFormOpen}
          onOpenChange={setIsCustomLocaleFormOpen}
          addLocale={({ id, customName }) => {
            setIsCustomLocaleFormOpen(false)
            onLocaleChange({ id, customName })
          }}
        />
      )}
    </>
  )
}

interface LocaleOptionProps {
  locales: Locale[]
}

function LocaleOptions(props: LocaleOptionProps) {
  return (
    <>
      {props.locales.map((locale) => (
        <SelectItem
          key={locale.id}
          value={locale.id}
          size="large"
          renderStartIcon={() => <Flag locale={locale.id} />}
        >
          {locale.label}
        </SelectItem>
      ))}
    </>
  )
}
